// Pinned-strip view mode: the three presentations and the one-time migration
// that renames the legacy one (#76).
//
// THE TRAP THIS MODULE EXISTS FOR. The strip shipped with two modes stored at
// `g1mt_tracker_pins_view`: 'table', and 'card' — but that 'card' rendered
// CompactPositionItem / CompactOrderItem, i.e. what #76 renames **Compact**.
// #76 adds a *real* Card mode (the PositionCard / OrderCard used by Open
// Positions and Open Orders) and makes it the default. So the literal string
// 'card' means different things either side of this change, and a stored
// 'card' must land on 'compact' — once — or every existing user silently jumps
// to the new, much taller cards on their next load.
//
// The naive fix — "read 'card', return 'compact'" — is WRONG and #76 says so
// explicitly: it would be a *permanent* reinterpretation, and a user who then
// picks the new Card mode could never save it (their 'card' would keep coming
// back as Compact). The migration therefore has to be one-time, which means it
// needs somewhere to record that it already ran.
//
// That marker is the key itself: the mode now lives at `..._view_v2`, and the
// legacy key is consulted ONLY while v2 is absent. Once v2 holds a valid mode,
// legacy is dead to us and 'card' means Card. One directional read, no flag to
// keep in sync, and the old key is left untouched so a rollback still finds it.

/** The three pinned presentations, in toolbar order — Card first.
 *
 *  Order is load-bearing (#76): Card leads because Open Orders presents Card
 *  before Table, and the strip's toggle must not contradict its neighbours.
 *  Compact — the mode the strip used to call 'card' — slots in front of Table.
 *  PINS_VIEW_MODES[0] is also the default; see resolvePinsViewMode. */
export const PINS_VIEW_MODES = ['card', 'compact', 'table'] as const;

export type PinsViewMode = (typeof PINS_VIEW_MODES)[number];

/** Where the mode lives now. The `_v2` suffix IS the migration marker: its
 *  presence means the legacy value has already been translated, so
 *  resolvePinsViewMode stops consulting the old key. Don't "tidy" this back
 *  onto the old key — that re-arms the migration on every read and pins users
 *  to Compact forever. */
export const PINS_VIEW_KEY = 'g1mt_tracker_pins_view_v2';

/** Pre-#76 key. Read once (while v2 is absent), never written. Left in place
 *  rather than deleted so rolling this change back restores the old behaviour
 *  instead of resetting everyone. */
export const LEGACY_PINS_VIEW_KEY = 'g1mt_tracker_pins_view';

export function isPinsViewMode(v: unknown): v is PinsViewMode {
  return typeof v === 'string' && (PINS_VIEW_MODES as readonly string[]).includes(v);
}

/**
 * Decide the strip's mode from what storage holds. Pure — callers supply the
 * two raw values, so this is testable without a DOM.
 *
 * **PRESENCE of v2 is the migration marker — not validity.** That distinction is
 * the whole contract:
 *
 *  1. v2 present and valid → use it. This is what lets a user *save* the new
 *     Card mode: after migration, 'card' reads back verbatim.
 *  2. v2 present but INVALID → 'card'. The migration has already run (something
 *     wrote that key), so the legacy value is stale and must not be consulted.
 *     #76: "an absent or invalid preference defaults to the new Card mode."
 *  3. v2 absent → the one-time migration, from the legacy key:
 *     - 'card'  → 'compact'  (the legacy 'card' WAS today's compact strip)
 *     - 'table' → 'table'    (unchanged; preserve the stored preference)
 *     - anything else → 'card'
 *
 * Reading legacy on an invalid v2 (the original shape of this function) is a
 * bug on two counts, both caught in review: it re-arms a migration that already
 * ran, and it can hand back Compact when the contract says invalid ⇒ Card. The
 * `stored != null` gate below is what separates "never migrated" from "migrated,
 * then the value got corrupted" — the two cases localStorage renders
 * indistinguishable if you only ask whether the value parses.
 */
export function resolvePinsViewMode(
  stored: string | null | undefined,
  legacy: string | null | undefined,
): PinsViewMode {
  if (isPinsViewMode(stored)) return stored;
  // v2 exists but doesn't parse ⇒ migration already happened; legacy is dead to
  // us. Default rather than resurrect it. (`''` counts as present: localStorage
  // returns null when a key is absent, so an empty string was written by someone.)
  if (stored != null) return PINS_VIEW_MODES[0];
  if (legacy === 'card') return 'compact';
  if (isPinsViewMode(legacy)) return legacy;
  return PINS_VIEW_MODES[0];
}

/**
 * Read the mode and, when the migration ran, persist the result so it runs
 * exactly once. Storage access is guarded: Safari private mode throws on
 * localStorage, and the strip must still render.
 *
 * Writing here (rather than leaving it to the caller's persist effect) is
 * deliberate — it closes the window where a user migrated to Compact, never
 * touched the toggle, and so never wrote v2, leaving the migration to re-run
 * on every load. Harmless while the rule is stable, but it would mean the
 * legacy key still steering the UI months from now.
 */
export function readPinsViewMode(): PinsViewMode {
  try {
    const stored = localStorage.getItem(PINS_VIEW_KEY);
    if (isPinsViewMode(stored)) return stored;
    const mode = resolvePinsViewMode(stored, localStorage.getItem(LEGACY_PINS_VIEW_KEY));
    localStorage.setItem(PINS_VIEW_KEY, mode);
    return mode;
  } catch {
    return PINS_VIEW_MODES[0];
  }
}

/**
 * Persist the mode to the v2 key. Lives here (not in the view) so every write
 * goes through the module that owns the migration contract above — a writer
 * that bypassed it could re-arm the legacy read. Guarded: Safari private mode
 * throws on localStorage, and the toggle must still work for the session.
 */
export function writePinsViewMode(mode: PinsViewMode): void {
  try {
    localStorage.setItem(PINS_VIEW_KEY, mode);
  } catch {
    // Private mode — the toggle still works for this session.
  }
}
